/**
 * Carbon Emissions Calculation Module
 *
 * Calculates annual and cumulative carbon emissions for heating systems
 * using evidence-based carbon intensity factors for UK energy sources.
 */

import type { SystemRecOutput, RecommendationOption } from './types';
import { resolveAssumptionsSafe, type ResolvedAssumption, type CarbonFactorsValues } from './assumptions';

// ==================== TYPES ====================

export interface CarbonEmissionsInput {
  annualHeatDemand_kWh: number;       // Total useful heat demand
  systemEfficiency: number;            // System efficiency (0-1 or SCOP for heat pumps)
  fuelType: 'gas' | 'electricity' | 'oil' | 'lpg';
  periodYears: number;                 // Analysis period
  solarSelfConsumptionRate?: number;  // 0-1 scale: portion of electricity from solar PV (zero carbon)
                                       // e.g., 0.30 = 30% from solar, 0.75 = 75% from solar+battery
}

export interface YearlyCarbonData {
  year: number;
  energyConsumed_kWh: number;         // Total energy consumed
  carbonIntensity_kgCO2perKWh: number; // Carbon intensity for this year
  emissions_kgCO2: number;             // Annual emissions
  cumulativeEmissions_kgCO2: number;   // Cumulative emissions to date
}

export interface CarbonEmissionsResult {
  fuelType: string;
  totalEmissions_kgCO2: number;        // Total over analysis period
  totalEmissions_tonnes: number;       // Total in tonnes
  annualAverage_kgCO2: number;         // Average per year
  yearlyBreakdown: YearlyCarbonData[];
}

export interface CarbonSavingsComparison {
  baseline: CarbonEmissionsResult;
  option: CarbonEmissionsResult;
  totalSaving_kgCO2: number;           // Total saving over period
  totalSaving_tonnes: number;          // Total saving in tonnes
  percentReduction: number;            // Percentage reduction
  annualSaving_kgCO2: number;          // Average annual saving
  yearlyComparison: Array<{
    year: number;
    baselineEmissions_kgCO2: number;
    optionEmissions_kgCO2: number;
    saving_kgCO2: number;
    cumulativeSaving_kgCO2: number;
  }>;
}

// ==================== CARBON INTENSITY FACTORS ====================

// Cached resolved carbon factors
let _cachedCarbonFactors: ResolvedAssumption<CarbonFactorsValues> | null = null;

/**
 * Get carbon factors from the assumptions registry.
 * Caches the result to avoid repeated lookups.
 */
export function getCarbonFactors(): ResolvedAssumption<CarbonFactorsValues> {
  if (_cachedCarbonFactors) {
    return _cachedCarbonFactors;
  }
  _cachedCarbonFactors = resolveAssumptionsSafe('carbon_factors');
  return _cachedCarbonFactors;
}

/**
 * UK Carbon Intensity Factors (evidence-based)
 *
 * Now loaded from the assumptions registry with fallback to inline constants.
 *
 * Sources:
 * - Gas: 0.183 kgCO2/kWh (DESNZ 2024/25, direct combustion)
 * - Electricity: Starting at 0.207 kgCO2/kWh (2024)
 *   with projected decarbonization trajectory
 * - Oil: 0.246 kgCO2/kWh (BEIS 2023)
 * - LPG: 0.214 kgCO2/kWh (BEIS 2023)
 *
 * @deprecated Use getCarbonFactors() for traceable assumptions.
 * This export is retained for backward compatibility.
 */
export const CARBON_INTENSITY = {
  get gas() {
    return getCarbonFactors().values.gas;
  },
  get oil() {
    return getCarbonFactors().values.oil;
  },
  get lpg() {
    return getCarbonFactors().values.lpg;
  },
  // Electricity grid - decarbonizing over time
  get electricity() {
    const grid = getCarbonFactors().values.electricity_grid;
    return {
      2024: grid['2024'],
      2025: grid['2025'],
      2026: grid['2026'],
      2030: grid['2030'],
      2040: grid['2040'],
      2050: grid['2050'],
    };
  },
} as const;

/**
 * Get electricity carbon intensity for a given year
 * Linear interpolation between known data points
 */
function getElectricityCarbonIntensity(year: number): number {
  const baseYear = 2024;
  const yearOffset = year - baseYear;
  
  if (yearOffset <= 0) return CARBON_INTENSITY.electricity[2024];
  if (yearOffset <= 1) {
    // 2024-2025: Linear interpolation
    const rate = (CARBON_INTENSITY.electricity[2025] - CARBON_INTENSITY.electricity[2024]) / 1;
    return CARBON_INTENSITY.electricity[2024] + (rate * yearOffset);
  }
  if (yearOffset <= 2) {
    // 2025-2026: Linear interpolation
    const rate = (CARBON_INTENSITY.electricity[2026] - CARBON_INTENSITY.electricity[2025]) / 1;
    return CARBON_INTENSITY.electricity[2025] + (rate * (yearOffset - 1));
  }
  if (yearOffset <= 6) {
    // 2026-2030: Linear interpolation
    const rate = (CARBON_INTENSITY.electricity[2030] - CARBON_INTENSITY.electricity[2026]) / 4;
    return CARBON_INTENSITY.electricity[2026] + (rate * (yearOffset - 2));
  }
  if (yearOffset <= 16) {
    // 2030-2040: Linear interpolation
    const rate = (CARBON_INTENSITY.electricity[2040] - CARBON_INTENSITY.electricity[2030]) / 10;
    return CARBON_INTENSITY.electricity[2030] + (rate * (yearOffset - 6));
  }
  if (yearOffset <= 26) {
    // 2040-2050: Linear interpolation
    const rate = (CARBON_INTENSITY.electricity[2050] - CARBON_INTENSITY.electricity[2040]) / 10;
    return CARBON_INTENSITY.electricity[2040] + (rate * (yearOffset - 16));
  }
  
  // Beyond 2050: assume stable at near-zero
  return CARBON_INTENSITY.electricity[2050];
}

/**
 * Get carbon intensity for a fuel type and year
 */
function getCarbonIntensity(fuelType: string, year: number): number {
  if (fuelType === 'electricity') {
    return getElectricityCarbonIntensity(year);
  }
  
  // Gas, oil, LPG are constant (fossil fuels don't decarbonize)
  if (fuelType === 'gas') return CARBON_INTENSITY.gas;
  if (fuelType === 'oil') return CARBON_INTENSITY.oil;
  if (fuelType === 'lpg') return CARBON_INTENSITY.lpg;
  
  // Default to gas if unknown
  return CARBON_INTENSITY.gas;
}

// ==================== CALCULATION FUNCTIONS ====================

/**
 * Calculate carbon emissions for a heating system over an analysis period
 *
 * When solarSelfConsumptionRate is provided for electricity systems:
 * - Solar-generated electricity has zero carbon emissions
 * - Effective carbon intensity = grid intensity * (1 - solarSelfConsumptionRate)
 * - e.g., 75% self-consumption means only 25% of electricity comes from grid
 */
export function calculateCarbonEmissions(input: CarbonEmissionsInput): CarbonEmissionsResult {
  const yearlyBreakdown: YearlyCarbonData[] = [];
  let cumulativeEmissions = 0;

  const currentYear = new Date().getFullYear();

  // Solar self-consumption reduces effective carbon intensity for electricity
  // Solar PV electricity has zero carbon emissions at point of use
  const solarRate = input.solarSelfConsumptionRate ?? 0;
  const gridPortion = input.fuelType === 'electricity' ? (1 - solarRate) : 1;

  for (let year = 1; year <= input.periodYears; year++) {
    // Calculate energy consumption for this year
    const energyConsumed = input.annualHeatDemand_kWh / input.systemEfficiency;

    // Get carbon intensity for this year (accounting for grid decarbonization)
    const baseCarbonIntensity = getCarbonIntensity(input.fuelType, currentYear + year);

    // Apply solar self-consumption reduction for electricity
    // Solar-generated electricity has 0 kgCO2/kWh, so we only count the grid portion
    const effectiveCarbonIntensity = baseCarbonIntensity * gridPortion;

    // Calculate emissions for this year
    const emissions = energyConsumed * effectiveCarbonIntensity;
    cumulativeEmissions += emissions;

    yearlyBreakdown.push({
      year,
      energyConsumed_kWh: energyConsumed,
      carbonIntensity_kgCO2perKWh: effectiveCarbonIntensity, // Effective intensity after solar offset
      emissions_kgCO2: emissions,
      cumulativeEmissions_kgCO2: cumulativeEmissions,
    });
  }

  const totalEmissions = cumulativeEmissions;

  return {
    fuelType: input.fuelType,
    totalEmissions_kgCO2: totalEmissions,
    totalEmissions_tonnes: totalEmissions / 1000,
    annualAverage_kgCO2: totalEmissions / input.periodYears,
    yearlyBreakdown,
  };
}

/**
 * Calculate carbon savings by comparing an option to baseline
 */
export function calculateCarbonSavings(
  baselineInput: CarbonEmissionsInput,
  optionInput: CarbonEmissionsInput
): CarbonSavingsComparison {
  const baseline = calculateCarbonEmissions(baselineInput);
  const option = calculateCarbonEmissions(optionInput);
  
  const totalSaving = baseline.totalEmissions_kgCO2 - option.totalEmissions_kgCO2;
  // Guard against division by zero to prevent NaN/Infinity in PDF reports
  const percentReduction = baseline.totalEmissions_kgCO2 > 0
    ? (totalSaving / baseline.totalEmissions_kgCO2) * 100
    : 0;
  
  // Build yearly comparison
  const yearlyComparison = baseline.yearlyBreakdown.map((baselineYear, index) => {
    const optionYear = option.yearlyBreakdown[index];
    const saving = baselineYear.emissions_kgCO2 - optionYear.emissions_kgCO2;
    
    return {
      year: baselineYear.year,
      baselineEmissions_kgCO2: baselineYear.emissions_kgCO2,
      optionEmissions_kgCO2: optionYear.emissions_kgCO2,
      saving_kgCO2: saving,
      cumulativeSaving_kgCO2: baselineYear.cumulativeEmissions_kgCO2 - optionYear.cumulativeEmissions_kgCO2,
    };
  });
  
  return {
    baseline,
    option,
    totalSaving_kgCO2: totalSaving,
    totalSaving_tonnes: totalSaving / 1000,
    percentReduction,
    annualSaving_kgCO2: totalSaving / baselineInput.periodYears,
    yearlyComparison,
  };
}

/**
 * Get fuel type for a system option
 */
function getFuelTypeForSystem(boiler: string): 'gas' | 'electricity' | 'oil' | 'lpg' {
  if (boiler === 'air_source_hp' || boiler === 'ground_source_hp') {
    return 'electricity';
  }
  // Default to gas for combi, system, regular boilers
  return 'gas';
}

/**
 * Get system efficiency for carbon calculations
 */
function getSystemEfficiency(option: RecommendationOption): number {
  // For heat pumps, use SCOP if available (typically 3.0-4.0)
  if (option.boiler === 'air_source_hp') {
    return 3.5; // Conservative SCOP estimate
  }
  if (option.boiler === 'ground_source_hp') {
    return 4.0; // GSHP typically higher efficiency
  }
  
  // For gas boilers, use ErP standard efficiency (92% manufacturer standard)
  // Apply slight variations by boiler type to differentiate carbon profiles:
  // - System boilers: 92% (baseline ErP)
  // - Regular boilers: 91.5% (slightly lower due to more heat loss in open vented systems)
  // - Combi boilers: 91% (cycling losses from on-demand operation)
  if (option.boiler === 'system') {
    return 0.92; // System boilers have better efficiency with cylinder storage
  }
  if (option.boiler === 'regular') {
    return 0.915; // Regular boilers have slight heat loss in open systems
  }
  if (option.boiler === 'combi' || option.boiler === 'storage_combi') {
    return 0.91; // Combis have cycling losses from frequent start-stops
  }
  
  // Default ErP standard for any other gas boiler type
  return 0.92;
}

/**
 * Calculate carbon emissions for all options in a recommendation output
 *
 * @param output - System recommendation output
 * @param periodYears - Analysis period in years (default: 20)
 * @param solarSelfConsumptionRate - Optional: portion of electricity from solar PV (0-1)
 *   - 0.30 = solar only (~30% self-consumption)
 *   - 0.75 = solar + battery (~75% self-consumption)
 *   - Solar-generated electricity has zero carbon, so this reduces emissions for heat pumps
 */
export function calculateCarbonForRecommendations(
  output: SystemRecOutput,
  periodYears: number = 20,
  solarSelfConsumptionRate: number = 0
): Map<string, CarbonEmissionsResult> {
  const results = new Map<string, CarbonEmissionsResult>();

  // Estimate annual heat demand if not available
  let annualHeatDemand = 12000; // Default estimate (kWh)

  // Try to get from LCOO data if available
  const primaryLcoo = output.primary?.lcoo;
  if (primaryLcoo && primaryLcoo.annualUsefulHeatKwh) {
    annualHeatDemand = primaryLcoo.annualUsefulHeatKwh;
  }

  // Calculate for all options
  const allOptions = [output.primary, ...(output.alternatives || [])];

  for (const option of allOptions) {
    const fuelType = getFuelTypeForSystem(option.boiler);
    const efficiency = getSystemEfficiency(option);

    // Only apply solar self-consumption to electricity-based systems
    // Gas/oil/LPG cannot benefit from solar PV
    const applicableSolarRate = fuelType === 'electricity' ? solarSelfConsumptionRate : 0;

    const emissions = calculateCarbonEmissions({
      annualHeatDemand_kWh: annualHeatDemand,
      systemEfficiency: efficiency,
      fuelType,
      periodYears,
      solarSelfConsumptionRate: applicableSolarRate,
    });

    results.set(option.id, emissions);
  }

  return results;
}

/**
 * Calculate carbon savings for all options compared to baseline
 *
 * @param output - System recommendation output
 * @param periodYears - Analysis period in years (default: 20)
 * @param solarSelfConsumptionRate - Optional: portion of electricity from solar PV (0-1)
 *   - Solar-generated electricity has zero carbon, improving heat pump savings
 */
export function calculateCarbonSavingsForRecommendations(
  output: SystemRecOutput,
  periodYears: number = 20,
  solarSelfConsumptionRate: number = 0
): Map<string, CarbonSavingsComparison> {
  const results = new Map<string, CarbonSavingsComparison>();

  // Need baseline to calculate savings
  if (!output.baselineResult) {
    return results;
  }

  // Estimate annual heat demand
  let annualHeatDemand = 12000; // Default
  const primaryLcoo = output.primary?.lcoo;
  if (primaryLcoo && primaryLcoo.annualUsefulHeatKwh) {
    annualHeatDemand = primaryLcoo.annualUsefulHeatKwh;
  }

  // Create baseline input (assume gas boiler at lower efficiency)
  // Use sludge-adjusted efficiency if available for accurate carbon calculations
  // Note: Baseline is existing gas system, so no solar benefit applied
  const baselineEfficiency = output.baselineResult.sludgeAdjustedEfficiency
    || output.baselineResult.initialEfficiency
    || 0.75;
  const baselineInput: CarbonEmissionsInput = {
    annualHeatDemand_kWh: annualHeatDemand,
    systemEfficiency: baselineEfficiency,
    fuelType: 'gas', // Assume existing system is gas
    periodYears,
    solarSelfConsumptionRate: 0, // Baseline gas cannot use solar
  };

  // Calculate savings for each option
  const allOptions = [output.primary, ...(output.alternatives || [])];

  for (const option of allOptions) {
    const fuelType = getFuelTypeForSystem(option.boiler);
    const efficiency = getSystemEfficiency(option);

    // Only apply solar self-consumption to electricity-based systems
    const applicableSolarRate = fuelType === 'electricity' ? solarSelfConsumptionRate : 0;

    const optionInput: CarbonEmissionsInput = {
      annualHeatDemand_kWh: annualHeatDemand,
      systemEfficiency: efficiency,
      fuelType,
      periodYears,
      solarSelfConsumptionRate: applicableSolarRate,
    };

    const savings = calculateCarbonSavings(baselineInput, optionInput);
    results.set(option.id, savings);
  }

  return results;
}
